import React, { useEffect, useRef, useState } from "react";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCamera,
  faSyncAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

import { resizeCanvasToDataUrl } from "../../utils/compressImage";

import "./CameraCaptureModal.sass";

export default function CameraCaptureModal({
  show = false,
  onHide = () => {},
  onCapture = () => {},
}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [facingMode, setFacingMode] = useState("user");
  const [error, setError] = useState("");
  const [isReady, setIsReady] = useState(false);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsReady(false);
  };

  const startStream = async () => {
    setError("");
    setIsReady(false);
    stopStream();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError(
        "Camera not supported in this browser. Please use a modern browser over HTTPS."
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          setIsReady(true);
        };
      }
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        setError(
          "Camera permission denied. Please allow camera access in your browser settings."
        );
      } else if (err?.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else {
        setError("Unable to access the camera. " + (err?.message ?? ""));
      }
    }
  };

  useEffect(() => {
    if (show) {
      startStream();
    } else {
      stopStream();
    }
    return () => {
      stopStream();
    };
  }, [show, facingMode]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !isReady) return;

    const canvas = document.createElement("canvas");
    const width = video.videoWidth;
    const height = video.videoHeight;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (facingMode === "user") {
      ctx.translate(width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, width, height);

    const compressed = resizeCanvasToDataUrl(canvas);
    onCapture(compressed);
    onHide();
  };

  const handleFlip = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  return (
    <Modal
      className="CameraCaptureModal"
      centered
      size="lg"
      show={show}
      onHide={onHide}
    >
      <Modal.Body>
        <FontAwesomeIcon
          icon={faTimes}
          className="close-icon"
          size="2x"
          onClick={onHide}
        />
        <h3 className="text-center mb-3">Take a Photo</h3>

        {error ? (
          <div className="camera-error text-danger text-center">{error}</div>
        ) : (
          <div className="camera-wrapper">
            <video
              ref={videoRef}
              className={
                "camera-video" + (facingMode === "user" ? " mirrored" : "")
              }
              autoPlay
              playsInline
              muted
            />
          </div>
        )}

        <div className="camera-actions">
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="light"
            onClick={handleFlip}
            disabled={!isReady}
            title="Switch camera"
          >
            <FontAwesomeIcon icon={faSyncAlt} />
          </Button>
          <Button variant="success" onClick={handleCapture} disabled={!isReady}>
            <FontAwesomeIcon icon={faCamera} className="mr-2" />
            Capture
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
